import { IProductDelivery, IShipmentData } from '../types/orders';
import { groupByKey } from './groupByKey';
import { groupBySku } from './groupBySku';

const getDestinationAndStatusKey = (shipment: IShipmentData): string => {
  const destinationKey = [
    shipment.locationId,
    shipment.deliveryAddress ?? '',
    shipment.city ?? '',
    shipment.zipCode ?? '',
    shipment.receiverInfo?.receiverName ?? ''
  ].join('|');

  return `${destinationKey}|${shipment.statusId}`;
};

/**
 * Agrupa los índices de shipments por mismo destino y mismo estado.
 * Los envíos con misma dirección/receptor y mismo estado cuentan como un solo "envío lógico".
 * @param shipments - Lista de envíos
 * @returns Array de grupos; cada grupo es un array de índices de shipments que comparten destino y estado
 */
export const getLogicalShipmentGroups = (shipments: IShipmentData[]): number[][] => {
  const groups = groupByKey(shipments, getDestinationAndStatusKey);
  return groups.map(group => group.map(shipment => shipments.indexOf(shipment)));
};

export interface IOrderedTrackingCardItem {
  shipmentIndex: number;
  productGroupIndex: number;
  productsInShippingGroup: IProductDelivery[];
  completeDataForEachGroupOfShipments: IShipmentData;
  optionalShipmentTitle: { current: number; total: number } | undefined;
}

/**
 * Construye la lista de cards de tracking ordenada por grupo lógico (mismo destino y estado).
 * Cada item representa un ShipmentCardTracking con sus datos y si debe mostrar el título "Envío X de Y".
 */
export const buildOrderedCardsForTracking = (
  shipments: IShipmentData[]
): IOrderedTrackingCardItem[] => {
  if (!shipments?.length) return [];

  const groups = getLogicalShipmentGroups(shipments);
  const ordered: IOrderedTrackingCardItem[] = [];

  groups.forEach((groupIndices, logicalGroupIndex) => {
    groupIndices.forEach(shipmentIndex => {
      const shipment = shipments[shipmentIndex];
      const productsBySku = groupBySku(shipment?.products || []);
      const isFirstShipmentOfLogicalGroup = groupIndices[0] === shipmentIndex;

      productsBySku.forEach((productsInShippingGroup, productGroupIndex) => {
        const isFirstCardOfLogicalGroup =
          isFirstShipmentOfLogicalGroup && productGroupIndex === 0;

        ordered.push({
          shipmentIndex,
          productGroupIndex,
          productsInShippingGroup,
          completeDataForEachGroupOfShipments: shipment,
          optionalShipmentTitle: isFirstCardOfLogicalGroup
            ? { current: logicalGroupIndex + 1, total: groups.length }
            : undefined
        });
      });
    });
  });

  return ordered;
};
